import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PencilIcon } from '@heroicons/react/24/outline';
import { formatDateBR } from '../../utils/dateUtils';
import { formatCPF, formatPhone } from '../../utils/formatters';
import {
  colaboradoresService,
  type Colaborador,
  type UpdateColaboradorDto,
} from '../../services/colaboradores.service';
import Modal from '../Modal';
import FormattedInput from '../FormattedInput';
import CpfInput from '../CpfInput';

interface FormacaoAcademicaItem {
  id: number;
  descricao?: string;
  valor?: string;
}

interface DadosPessoaisTabProps {
  colaborador: Colaborador;
  formacoes: FormacaoAcademicaItem[];
}

export default function DadosPessoaisTab({ colaborador, formacoes }: DadosPessoaisTabProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState<UpdateColaboradorDto>({
    nome: colaborador.nome,
    nome_social: colaborador.nome_social || '',
    nome_mae: colaborador.nome_mae || '',
    pcd: colaborador.pcd ?? false,
    enviar_esocial: colaborador.enviar_esocial ?? true,
    regime_contratacao: colaborador.regime_contratacao || '',
    estado_civil: colaborador.estado_civil || '',
    cpf: colaborador.cpf,
    rg: colaborador.rg || '',
    data_nascimento: colaborador.data_nascimento ? colaborador.data_nascimento.split('T')[0] : '',
    sexo: colaborador.sexo || '',
    telefone: colaborador.telefone || '',
    email: colaborador.email || '',
    matricula_esocial: colaborador.matricula_esocial || '',
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateColaboradorDto) =>
      colaboradoresService.update(colaborador.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['colaborador', colaborador.id] });
      setIsEditing(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return formatDateBR(dateString);
    } catch {
      return 'N/A';
    }
  };

  const getFormacaoAcademicaNome = (id?: number | null) => {
    if (!id) return 'N/A';
    const formacao = formacoes.find((f) => f.id === id);
    return formacao?.descricao || formacao?.valor || 'N/A';
  };

  const resetForm = () => {
    setFormData({
      nome: colaborador.nome,
      nome_social: colaborador.nome_social || '',
      nome_mae: colaborador.nome_mae || '',
      pcd: colaborador.pcd ?? false,
      enviar_esocial: colaborador.enviar_esocial ?? true,
      regime_contratacao: colaborador.regime_contratacao || '',
      estado_civil: colaborador.estado_civil || '',
      cpf: colaborador.cpf,
      rg: colaborador.rg || '',
      data_nascimento: colaborador.data_nascimento ? colaborador.data_nascimento.split('T')[0] : '',
      sexo: colaborador.sexo || '',
      telefone: colaborador.telefone || '',
      email: colaborador.email || '',
      matricula_esocial: colaborador.matricula_esocial || '',
    });
  };

  return (
    <div className="py-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Dados do Colaborador</h3>
        <button
          onClick={() => {
            resetForm();
            setIsEditing(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <PencilIcon className="w-4 h-4" />
          Editar Dados
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6 border border-gray-100">
          <h4 className="font-medium mb-4 text-gray-700">Informações Básicas</h4>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Nome Completo</dt>
              <dd className="mt-1 text-sm text-gray-900 font-medium">{colaborador.nome}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Nome Social</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.nome_social || 'Não informado'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Nome da Mãe</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.nome_mae || 'Não informado'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">PCD</dt>
              <dd className="mt-1 text-sm">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                    colaborador.pcd
                      ? 'bg-purple-100 text-purple-800 border border-purple-200'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {colaborador.pcd ? 'Sim (PCD)' : 'Não'}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Enviar para o eSocial</dt>
              <dd className="mt-1 text-sm">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                    colaborador.enviar_esocial !== false
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-amber-100 text-amber-800 border border-amber-200'
                  }`}
                >
                  {colaborador.enviar_esocial !== false ? 'Sim (Habilitado)' : 'Não (Ignorar no eSocial)'}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Estado Civil</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.estado_civil || 'Não informado'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">CPF</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatCPF(colaborador.cpf)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">RG</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.rg || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Data de Nascimento</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(colaborador.data_nascimento)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Sexo</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {colaborador.sexo === 'M'
                  ? 'Masculino'
                  : colaborador.sexo === 'F'
                  ? 'Feminino'
                  : colaborador.sexo || 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Formação Acadêmica</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {getFormacaoAcademicaNome(colaborador.formacao_academica_id)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-100">
          <h4 className="font-medium mb-4 text-gray-700">Contato</h4>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.email || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Telefone</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {colaborador.telefone ? formatPhone(colaborador.telefone) : 'N/A'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-100">
          <h4 className="font-medium mb-4 text-gray-700">Informações Profissionais</h4>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Cargo</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.cargo?.nome || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Setor</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.setor?.nome || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Regime de Contratação</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.regime_contratacao || 'Não informado'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Data de Admissão</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(colaborador.data_admissao)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Matrícula e-Social</dt>
              <dd className="mt-1 text-sm text-gray-900">{colaborador.matricula_esocial || 'N/A'}</dd>
            </div>
            {colaborador.data_demissao && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Data de Demissão</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(colaborador.data_demissao)}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Modal de Edição */}
      <Modal
        isOpen={isEditing}
        onClose={() => {
          setIsEditing(false);
          resetForm();
        }}
        title="Editar Dados do Colaborador"
        size="lg"
        footer={
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                resetForm();
              }}
              className="btn-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={updateMutation.isPending}
              className="btn-primary flex-1 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.nome || ''}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Social
              </label>
              <input
                type="text"
                value={formData.nome_social || ''}
                onChange={(e) => setFormData({ ...formData, nome_social: e.target.value })}
                className="input-field"
                placeholder="Nome social preferencial"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo da Mãe
              </label>
              <input
                type="text"
                value={formData.nome_mae || ''}
                onChange={(e) => setFormData({ ...formData, nome_mae: e.target.value })}
                className="input-field"
                placeholder="Nome completo da mãe"
              />
            </div>
            <div>
              <CpfInput
                label="CPF"
                required
                value={formData.cpf || ''}
                onChange={(value) => setFormData({ ...formData, cpf: value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                RG
              </label>
              <input
                type="text"
                value={formData.rg || ''}
                onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Nascimento
              </label>
              <input
                type="date"
                value={formData.data_nascimento || ''}
                onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sexo
              </label>
              <select
                value={formData.sexo || ''}
                onChange={(e) => setFormData({ ...formData, sexo: e.target.value })}
                className="input-field"
              >
                <option value="">Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PCD
              </label>
              <select
                value={formData.pcd ? 'sim' : 'nao'}
                onChange={(e) => setFormData({ ...formData, pcd: e.target.value === 'sim' })}
                className="input-field"
              >
                <option value="nao">Não</option>
                <option value="sim">Sim (PCD)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enviar para o eSocial
              </label>
              <select
                value={formData.enviar_esocial ? 'sim' : 'nao'}
                onChange={(e) => setFormData({ ...formData, enviar_esocial: e.target.value === 'sim' })}
                className="input-field"
              >
                <option value="sim">Sim (Habilitado)</option>
                <option value="nao">Não (Ignorar no eSocial)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regime de Contratação
              </label>
              <select
                value={formData.regime_contratacao || ''}
                onChange={(e) => setFormData({ ...formData, regime_contratacao: e.target.value })}
                className="input-field"
              >
                <option value="">Selecione...</option>
                <option value="CLT">CLT</option>
                <option value="PJ">PJ</option>
                <option value="Estágio">Estágio</option>
                <option value="Cooperado">Cooperado</option>
                <option value="Temporário">Temporário</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado Civil
              </label>
              <select
                value={formData.estado_civil || ''}
                onChange={(e) => setFormData({ ...formData, estado_civil: e.target.value })}
                className="input-field"
              >
                <option value="">Selecione...</option>
                <option value="Solteiro(a)">Solteiro(a)</option>
                <option value="Casado(a)">Casado(a)</option>
                <option value="Divorciado(a)">Divorciado(a)</option>
                <option value="Viúvo(a)">Viúvo(a)</option>
                <option value="União Estável">União Estável</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <FormattedInput
                type="phone"
                value={formData.telefone || ''}
                onChange={(value) => setFormData({ ...formData, telefone: value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-field"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matrícula e-Social
              </label>
              <input
                type="text"
                value={formData.matricula_esocial || ''}
                onChange={(e) => setFormData({ ...formData, matricula_esocial: e.target.value })}
                className="input-field"
              />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
